import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * 保存同步指示器状态（两态）
 *
 * saving:  saveNow 通过全部门控、实际写入 collection —— "正在保存"
 * saved:   tx.isPersisted 解析（Electric 服务端确认落库）—— "保存成功"闪现 2s 后清除
 *
 * 上报方：CRDTNoteEditorPanel（persisted/clear）+ TipTapEditor saveNow
 * （saving，经 onNoteSaving 回调；程序化编辑的静默保存不回调）。
 *
 * 渲染方（一套组件 SaveStatusIndicator，唯一位置）：
 * - 全局 Header 右区首位（原"应用版本"位置）：桌面/移动统一。
 *   多编辑器（主/速记/左参考/右参考）并发时按 lastActiveNoteId
 *   （最近一次保存的笔记）显示，避免一处位置多状态竞争。
 *
 * 资源：仅一个 Record<noteId, status> + 一个 lastActiveNoteId，dispatch 频率 =
 * 每次实际保存一次 + 每次云端持久化确认一次，可忽略不计。不持久化。
 */
export type NoteSaveStatus = 'saving' | 'saved' | 'failed'
export type NoteSaveFailReason = 'conflict' | 'error' | 'quota'

interface SaveIndicatorState {
  /** noteId → 状态。无键 = 无指示（idle） */
  status: Record<string, NoteSaveStatus>
  /** noteId → 失败原因（failed 态的文案依据）：
   *  conflict = OCC 冲突（内容已在其他设备更新，本地编辑已让位显示最新）
   *  error    = 网络/持久化失败（保持 EDITING 将自动重试，编辑仍在本地）
   *  quota    = 库配额超限（★ 2026-10-29：永久错误不重试，编辑仍在本地，提示清理/升级） */
  failReason: Record<string, NoteSaveFailReason>
  /** 最近活跃笔记（noteSaving/notePersisted/noteFailed 时更新）：
   *  Header 唯一指示器位置的显示锚点——多编辑器并发时取最近保存的那个 */
  lastActiveNoteId: string | null
}

const initialState: SaveIndicatorState = {
  status: {},
  failReason: {},
  lastActiveNoteId: null,
}

const saveIndicatorSlice = createSlice({
  name: 'saveIndicator',
  initialState,
  reducers: {
    /** 实际保存开始（saveNow 通过全部门控、即将写入 collection） */
    noteSaving: (state, action: PayloadAction<string>) => {
      state.status[action.payload] = 'saving'
      delete state.failReason[action.payload]
      state.lastActiveNoteId = action.payload
    },
    /** 云端持久化确认（tx.isPersisted 解析）："保存成功"闪现，由上报方 2s 后 noteFlashEnd */
    notePersisted: (state, action: PayloadAction<string>) => {
      state.status[action.payload] = 'saved'
      state.lastActiveNoteId = action.payload
    },
    /** "保存成功"闪现结束：仅清除 'saved'（闪现期间新一轮保存 → 'saving' 不受影响） */
    noteFlashEnd: (state, action: PayloadAction<string>) => {
      if (state.status[action.payload] === 'saved') {
        delete state.status[action.payload]
      }
    },
    /** 保存失败（OCC 冲突/网络失败）：保持显示直至下次保存开始或笔记切换（失败不闪没） */
    noteFailed: (state, action: PayloadAction<{ noteId: string; reason: NoteSaveFailReason }>) => {
      const { noteId, reason } = action.payload
      state.status[noteId] = 'failed'
      state.failReason[noteId] = reason
      state.lastActiveNoteId = noteId
    },
    /** 无条件清除（笔记切换/组件卸载/兜底超时） */
    noteCleared: (state, action: PayloadAction<string>) => {
      delete state.status[action.payload]
      delete state.failReason[action.payload]
      if (state.lastActiveNoteId === action.payload) {
        state.lastActiveNoteId = null
      }
    },
  },
})

export const {
  noteSaving,
  notePersisted,
  noteFlashEnd,
  noteFailed,
  noteCleared,
} = saveIndicatorSlice.actions
export default saveIndicatorSlice.reducer
